import {
    EffectComposer,
    EffectPass,
    FXAAEffect,
    HueSaturationEffect,
    RenderPass,
    Selection,
    ToneMappingEffect,
    ToneMappingMode,
} from 'postprocessing';
import { HalfFloatType } from 'three';
import { nextTick, reactive, watch } from 'vue';

import { usePane, useRenderer, useResize } from '@resn/gozer-vue';

import { useBloomContext } from '~/composables/useBloom';
import { LAYER_BG } from '~/core/constants';
import { createLogger } from '~/utils/logger';

const logger = createLogger('scene-composer');

/** Exposure + saturation only. Avoid pmndrs BrightnessContrastEffect — see comment in recreateEffectPass. */
const IMAGE_PROCESSING_DEFAULTS = {
    enabled: true,
    exposure: 1.2,
    saturation: 0.05,
};

export const useSceneComposerPmndrs = ({
    renderer: rendererProp = null,
    bloom = true,
    fxaa = false,
    msaa = false,
} = {}) => {
    const gl = useRenderer();
    const getWebGLRenderer = () => rendererProp ?? gl?.renderer ?? null;

    let _composer = null;

    const ensureComposer = () => {
        const renderer = getWebGLRenderer();
        if (!renderer) return null;

        if (!_composer) {
            _composer = new EffectComposer(renderer, {
                frameBufferType: HalfFloatType,
                multisampling: msaa ? 4 : 0, // MSAA AntiAliasing - 2 to 4 for clean FXAA, Remove if not needed for performance
            });
            _composer.autoRenderToScreen = true;
        } else if (_composer.getRenderer() !== renderer) {
            _composer.setRenderer(renderer);
        }

        return _composer;
    };

    const type = 'pmndrs';

    let mainCamera = null;

    const bloomContext = useBloomContext();

    const propsImageProcessing = reactive({ ...IMAGE_PROCESSING_DEFAULTS });

    // Effect toggle states
    const propsEffects = reactive({
        ...(fxaa && { fxaaEnabled: true }),
        ...(bloom && { bloomEnabled: bloomContext.enabled.value }),
        toneMappingEnabled: true,
    });
    let effectPass = null;
    let fxaaPass = null;

    // Store effect instances
    let effectFXAA = null;
    let effectBloom = null;
    let effectToneMapping = null;
    let effectHueSaturation = null;

    const syncImageProcessing = () => {
        if (effectHueSaturation) {
            effectHueSaturation.saturation = propsImageProcessing.saturation;
        }

        const renderer = getWebGLRenderer();

        if (renderer) {
            renderer.toneMappingExposure = propsImageProcessing.exposure;
        }
    };

    const setup = ({ scene, camera: cam, orthoCamera } = {}) => {
        const composer = ensureComposer();

        if (!composer) {
            logger.warn('Scene composer setup missing WebGL renderer', {
                event: 'argument.missing',
                consumer: 'useSceneComposerPmndrs',
            });
            return;
        }

        reset();

        if (!scene || !cam || !orthoCamera) {
            logger.warn('Scene composer setup arguments missing', {
                event: 'argument.missing',
                consumer: 'useSceneComposerPmndrs',
                sceneMissing: !scene,
                cameraMissing: !cam,
                orthoCameraMissing: !orthoCamera,
            });
            return;
        }
        // Store camera reference
        mainCamera = cam;
        // mainCamera.far = 16;
        mainCamera.far = 50;

        // Background pass
        const passRenderBg = new RenderPass(scene, orthoCamera);
        passRenderBg.selection = new Selection([], LAYER_BG);
        passRenderBg.name = 'RenderPassBG';

        // Main scene pass
        const passRenderScene = new RenderPass(scene, mainCamera);
        passRenderScene.clearPass.setClearFlags(false, true, false);
        passRenderScene.name = 'RenderPassScene';

        // Create the effect instances
        if (fxaa) effectFXAA = new FXAAEffect();
        if (bloom) effectBloom = bloomContext.init(scene, mainCamera);

        effectToneMapping = new ToneMappingEffect({ mode: ToneMappingMode.NEUTRAL });
        effectHueSaturation = new HueSaturationEffect();

        syncImageProcessing();

        // Add the render passes
        composer.addPass(passRenderBg);
        composer.addPass(passRenderScene);

        recreateEffectPass();

        resize(viewport);
        // default.vue applies maxDpr in a ref watch that runs after child onMounted;
        // defer one tick so composer buffers match the final drawing buffer size.
        nextTick(() => resize(viewport));
    };

    // FXAA must be in its own EffectPass because it samples neighboring texels.
    // When merged with tone mapping, FXAA reads pre-tone-mapped HDR values for
    // neighbors while receiving tone-mapped values for the current pixel — this
    // mismatch blows out bright additive-blended content (e.g. ShineLayer).
    const recreateEffectPass = () => {
        if (!mainCamera || !_composer) return;

        if (effectPass !== null) {
            _composer.removePass(effectPass);
            effectPass.dispose();
            effectPass = null;
        }

        if (fxaaPass !== null) {
            _composer.removePass(fxaaPass);
            fxaaPass.dispose();
            fxaaPass = null;
        }

        const effects = [];

        if (propsEffects.bloomEnabled && effectBloom) effects.push(effectBloom);

        if (propsEffects.toneMappingEnabled && effectToneMapping) effects.push(effectToneMapping);

        if (propsImageProcessing.enabled && effectHueSaturation) {
            effects.push(effectHueSaturation);
        }

        if (effects.length > 0) {
            effectPass = new EffectPass(mainCamera, ...effects);
            effectPass.name = 'CombinedEffects';
            _composer.addPass(effectPass);
        }

        if (propsEffects.fxaaEnabled && effectFXAA) {
            fxaaPass = new EffectPass(mainCamera, effectFXAA);
            fxaaPass.name = 'FXAA';
            _composer.addPass(fxaaPass);
        }
    };

    const updateCamera = (cam) => {
        if (!cam) return;

        mainCamera = cam;

        recreateEffectPass();
    };

    const render = () => {
        _composer?.render();
    };

    const resize = ({ width, height }) => {
        if (!_composer || !width || !height) return;

        _composer.setSize(width, height);
    };

    const reset = () => {
        _composer?.removeAllPasses();
    };

    const viewport = useResize(resize);

    // Watch effect toggles and recreate pass when needed
    watch(propsEffects, () => recreateEffectPass());

    watch(
        () => propsImageProcessing.enabled,
        () => recreateEffectPass()
    );

    watch(propsImageProcessing, () => syncImageProcessing(), { deep: true });

    watch(
        bloomContext.enabled,
        (enabled) => {
            propsEffects.bloomEnabled = enabled;
            recreateEffectPass();
        },
        { immediate: true }
    );

    usePane([{ value: propsEffects }], { title: 'Composer Effects', expanded: false });

    usePane([{ value: propsImageProcessing }], {
        title: 'Image Processing',
        expanded: false,
    });

    return {
        get composer() {
            return _composer;
        },
        setup,
        render,
        type,
        updateCamera,
        reset,
        propsImageProcessing,
    };
};
